import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { EyWorksDatePicker } from "eyworks-ui-components";
import { useNavigate, useParams } from "react-router-dom";
import { CustomButton, CustomTextInput, Footer } from "src/app/components";
import { store, useAppDispatch } from "src/app/services/store";
import { EymemoryRoutes } from "src/app/navigation/routes";
import { handleError, tryCatchWrapper } from "src/app/utilities/helpers";
import {
  trackOrderStatus,
  validateOrder,
} from "src/shared/services/src/lib/eymemory-orders";
import {
  EymemoriesProductTypes,
  EymemoriesStageSteps,
  GenericResponse,
  ValidateOrderResponse,
} from "src/shared/types/src";
import { toast } from "react-toastify";
import {
  APP_MESSAGES,
  DATE_FORMATS,
} from "src/shared/config/src/lib/constants";
import { isValidEmail } from "src/shared/utils/src";
import LocalImages from "../../static";
import {
  eymemoryLogout,
  logout,
  performAfterLoginEymemoriesOrder,
} from "src/shared/redux-state/src";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const ValidateOrder: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { orderHash = "" } = useParams<{ orderHash: string }>();
  // const [dob, setDob] = useState<Date>(new Date("04-01-2021"));
  const [dob, setDob] = useState<Date>();
  const [email, setEmail] = useState<string>("");
  const [loader, setLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorData, setErrorData] = useState<{
    dob: string;
    email: string;
    captchaToken: string;
  }>({ dob: "", email: "", captchaToken: "" });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    document.title = 'eyworks | Parent Portal';
    // dispatch(logout({ reduxStore: store }));
    dispatch(eymemoryLogout({orderHash}));
    if (!orderHash) navigate(EymemoryRoutes.EYMEMORIES_ERROR);
  }, []);

  const generateCaptcha = async () => {
    if (!executeRecaptcha) {
      console.warn("recaptcha is not yet available");
      return;
    }
    const token = await executeRecaptcha("submit_action");
    setCaptchaToken(token);
    console.info("recaptcha loaded");
    return token;
  }

  useEffect(() => {
    (async () => await generateCaptcha())();
  }, [executeRecaptcha]);

  const handleSubmit = async () => {
    setShowError(true);
    setLoader(true);

    await tryCatchWrapper(
      async () => {
        const newCaptchaToken = await generateCaptcha();

        const err = { dob: "", email: "", captchaToken: "" };
        if (!dob) err.dob = "This field is required.";
        if (!email.trim()) err.email = "This field is required.";
        if (email && !isValidEmail(email)) err.email = "Invalid email";
        if (!captchaToken || !newCaptchaToken)
          err.captchaToken =
            "Captcha is not valid, please refresh and try again.";
        if (Object.values(err)?.some((e: string) => e.length)) {
          setErrorData(err);
          return null;
        }

        await validateOrder({
          dob: format(dob as Date, DATE_FORMATS.YMD),
          email,
          orderHash,
          captchaToken: (newCaptchaToken || captchaToken) as string,
          callback: async (
            response: GenericResponse<ValidateOrderResponse>
          ) => {
            if (response?.status === "success") {
              await dispatch(
                performAfterLoginEymemoriesOrder({
                  validateOrderResponse: response?.data,
                  orderHash,
                })
              );
              if (
                response?.data?.order?.productType ===
                EymemoriesProductTypes.DOWNLOAD_LINK
              ) {
                navigate(`${EymemoryRoutes.EYMEMORIES_DOWNLOAD_FROM_LINK}/${orderHash}`, {
                  state: {
                    downloadUrl: response?.data?.order?.meta?.downloadLink,
                    orderHash,
                  },
                });
              }
              await trackOrderStatus({
                status: EymemoriesStageSteps.VIEWED,
                orderHash
              });
              return null;
            }
            toast.error(APP_MESSAGES.generalErrorMessage);
          },
        });
      },
      {
        onFinally: () => {
          setLoader(false);
        },
        onError: async (error: any) => {
          await generateCaptcha();
          if (
            error instanceof AxiosError &&
            error?.response?.data?.validation_errors &&
            Object.keys(error?.response?.data?.validation_errors)?.length
          ) {
            const validationError = error?.response?.data?.validation_errors;
            setErrorData({
              ...errorData,
              ...(validationError?.dob?.length && {
                dob: validationError?.dob.join(),
              }),
              ...(validationError?.email?.length && {
                email: validationError?.email.join(),
              }),
              ...(validationError?.captchaToken?.length && {
                captchaToken: validationError?.captchaToken.join(),
              }),
            });
          } else {
            if (error instanceof AxiosError) {
              // Handle Axios errors
              if (error.response) {
                const status = error.response?.status;
                if (status === 403) {
                  return navigate(
                    `${EymemoryRoutes.EYMEMORIES_FORBIDDEN}?orderHash=${orderHash}`
                  );
                }
              }
            }
            handleError(error, {orderHash});
          }
        },
      }
    );
  };

  const orderHTMLTemplate = () => {
    return (
      <div>
        <p className="fs-5 fs-18 text-black fw-bold mg-b-5">
          Your eymemories keepsake is just a click away!
        </p>
        <p className="fs-14 text-black mg-b-md-28 mg-b-xs-20">
          To access your order, please enter your email address and child’s date
          of birth.
        </p>
        <div className="mg-b-md-25 mg-b-xs-20 position-relative">
          <CustomTextInput
            type={"email"}
            name={"email"}
            value={email}
            onChange={(e) => {
              const emailTxt = e?.target?.value?.trim() || "";
              setEmail(emailTxt);
              setErrorData({
                ...errorData,
                email: !emailTxt
                  ? "This field is required"
                  : isValidEmail(emailTxt)
                  ? ""
                  : "Invalid email",
              });
            }}
            label={"Email"}
          />
          {showError && errorData?.email && (
            <span className="form-error floating-error">
              {errorData?.email}
            </span>
          )}
        </div>
        <div className="form-group mg-b-25 position-relative">
          <label className="form-label fs-12">Child’s Date of Birth</label>
          <EyWorksDatePicker
            customDateValue={dob ? format(dob, "d MMM yyyy") : ""}
            onChange={(e: Date) => setDob(e)}
            selectedDate={dob}
          />
          {showError && errorData?.dob && (
            <span className="form-error floating-error">{errorData?.dob}</span>
          )}
        </div>
        {showError && errorData?.captchaToken && (
          <div className="form-group mg-b-25">
            <span className="form-error">{errorData?.captchaToken}</span>
          </div>
        )}
        <CustomButton
          text="Submit"
          color="primary"
          onClick={handleSubmit}
          isLoading={loader}
        />
      </div>
    );
  };

  return (
    <div className="main-wrapper bg-white container">
      <div className="d-flex flex-column flex-grow-1 mg-b-20 justify-content-md-center">
        <div className="mg-b-md-30 mg-b-xs-20 mg-t-md-100 mg-t-xs-50 pd-t-xs-0 d-flex justify-content-center flex-column align-items-center">
            <img
              src={LocalImages.eyMemoriesLogo}
              alt="logo"
              className="eymemories-logo"
            />
            <img
              src={LocalImages.eyMemoriesAbstract}
              alt="download icon"
              className="download-icon mg-t-md-30 mg-t-xs-15"
            />
          </div>
          <div className="flex-1 d-flex flex-column justify-content-center">
            {orderHTMLTemplate()}
          </div>
      </div>
      <div className="mg-b-10">
        <Footer />
      </div>
    </div>
  );
};

export default ValidateOrder;
